import { useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Calendar, ArrowLeft, Clock, Phone } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { HeroBackground } from "@/components/site/HeroBackground";
import { pickPostHeroImage } from "@/data/site/stockImages";
import { PageSEO } from "@/components/site/PageSEO";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { CTASection } from "@/components/site/CTASection";
import { QuickAnswer } from "@/components/site/QuickAnswer";
import { ReviewedBy } from "@/components/site/ReviewedBy";
import { AuthorBio } from "@/components/site/AuthorBio";
import { AuthoritySources } from "@/components/site/AuthoritySources";
import { RichContent } from "@/components/site/RichContent";
import { RelatedPosts } from "@/components/site/RelatedPosts";
import { BlogPostSidebar } from "@/components/site/BlogPostSidebar";
import { blogPosts as staticBlogPosts, type BlogPost } from "@/data/site/blog";
import { wpPostsQueryOptions } from "@/routes/blog.index";
import { brand } from "@/data/site/brand";
import { extractFaqsFromHtml, extractFaqsFromSections, buildFaqPageSchema } from "@/lib/faqExtract";


const formatDate = (d: string) =>
  new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

const wordCount = (text: string) => text.trim().split(/\s+/).length;

import { founder } from "@/data/site/brand";

const BlogPostPage = () => {
  const { slug = "" } = useParams();
  const { data: wpPosts } = useSuspenseQuery(wpPostsQueryOptions);

  const { post, related } = useMemo(() => {
    const wpSlugs = new Set(wpPosts.map((p) => p.slug));
    const all: BlogPost[] = [
      ...wpPosts,
      ...staticBlogPosts.filter((p) => !wpSlugs.has(p.slug)),
    ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    const found = all.find((p) => p.slug === slug);
    if (!found) return { post: undefined, related: [] as BlogPost[] };
    const explicit = (found.relatedSlugs ?? [])
      .map((s) => all.find((p) => p.slug === s))
      .filter((p): p is BlogPost => !!p)
      .slice(0, 3);
    const seen = new Set([found.slug, ...explicit.map((p) => p.slug)]);
    const sameCat = all.filter((p) => !seen.has(p.slug) && p.category === found.category);
    const others = all.filter((p) => !seen.has(p.slug) && p.category !== found.category);
    return { post: found, related: [...explicit, ...sameCat, ...others].slice(0, 3) };
  }, [wpPosts, slug]);

  if (!post) return <Navigate to="/blog" replace />;

  const path = `/blog/${post.slug}`;
  const url = `${brand.siteUrl}${path}`;

  // Resolve a real, shipped image. Blog data references /blog-images/* paths
  // that don't exist in the build — fall back to curated hero stock images.
  const heroImage = pickPostHeroImage(post);
  const resolvedImage =
    post.image && !post.image.startsWith("/blog-images/") ? post.image : heroImage.src;

  // Compute approximate word count from whichever content source is present.
  const bodyText =
    post.body ||
    (post.bodyHtml ? post.bodyHtml.replace(/<[^>]+>/g, " ") : "") ||
    (post.sections ?? []).flatMap((s) => [s.heading ?? "", ...s.paragraphs]).join(" ");
  const words = bodyText.trim() ? wordCount(bodyText) : 0;



  const authorName = post.author.split(",")[0].trim();
  const authorCreds = post.author.includes(",")
    ? post.author.split(",").slice(1).join(",").trim()
    : founder.credentials;

  const authorPerson = {
    "@type": "Person",
    name: authorName,
    jobTitle: founder.title,
    description: founder.bio,
    url: `${brand.siteUrl}/about/franklin-sofi`,
    image: `${brand.siteUrl}${founder.image}`,
    sameAs: [`${brand.siteUrl}/about/franklin-sofi`],
    knowsAbout: [
      "IRS tax resolution",
      "Offer in Compromise",
      "IRS audit defense",
      "Wage garnishment release",
      "Payroll tax liabilities",
    ],
    hasCredential: founder.credentialsList.map((c) => ({
      "@type": "EducationalOccupationalCredential",
      credentialCategory: c,
    })),
  };

  // BlogPosting with E-E-A-T fields for YMYL finance content.
  const articleSchema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": ["BlogPosting", "Article"],
    headline: post.title,
    description: post.meta.description,
    datePublished: post.date,
    dateModified: post.dateModified ?? post.date,
    author: authorPerson,
    reviewedBy: authorPerson,
    lastReviewed: post.dateModified ?? post.date,
    editor: authorPerson,
    publisher: {
      "@type": "Organization",
      name: brand.legalName,
      url: brand.siteUrl,
      logo: {
        "@type": "ImageObject",
        url: `${brand.siteUrl}/og-default.jpg`,
      },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    articleSection: post.category,
    wordCount: words,
    inLanguage: "en-US",
    isAccessibleForFree: true,
    isFamilyFriendly: true,
    about: { "@type": "Thing", name: post.category },
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: [".speakable", "h1", "h2"],
    },
    citation: (post.sources ?? []).map((s) => ({
      "@type": "CreativeWork",
      name: s.label,
      url: s.url,
      ...(s.publisher ? { publisher: { "@type": "Organization", name: s.publisher } } : {}),
    })),
    image: {
      "@type": "ImageObject",
      url: resolvedImage.startsWith("http")
        ? resolvedImage
        : `${brand.siteUrl}${resolvedImage}`,
      width: 1200,
      height: 630,
    },

  };

  const schemas: object[] = [articleSchema];

  // Build FAQPage schema from any Q&A structure present in the post.
  const faqPairs = [
    ...(post.quickAnswer
      ? [{ question: post.title.endsWith("?") ? post.title : `${post.title}?`, answer: post.quickAnswer.answer }]
      : []),
    ...(post.bodyHtml ? extractFaqsFromHtml(post.bodyHtml) : []),
    ...(!post.bodyHtml && post.sections ? extractFaqsFromSections(post.sections) : []),
  ];
  // Dedupe by lowercased question.
  const seenQ = new Set<string>();
  const uniqueFaqs = faqPairs.filter((f) => {
    const k = f.question.toLowerCase();
    if (seenQ.has(k)) return false;
    seenQ.add(k);
    return true;
  });
  if (uniqueFaqs.length > 0) {
    schemas.push(buildFaqPageSchema(uniqueFaqs));
  }


  return (
    <SiteLayout>
      <PageSEO
        title={post.meta.title}
        description={post.meta.description}
        path={path}
        image={resolvedImage}
        ogType="article"
        publishedTime={post.date}
        modifiedTime={post.dateModified ?? post.date}
        author={post.author}
        breadcrumbs={[
          { name: "Home", href: "/" },
          { name: "Blog", href: "/blog" },
          { name: post.title, href: path },
        ]}
        jsonLd={schemas}
      />

      <section className="relative bg-gradient-navy text-white overflow-hidden">
        <HeroBackground image={pickPostHeroImage(post)} priority />
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12 lg:py-28 max-w-6xl">
          <Breadcrumbs
            items={[
              { name: "Home", href: "/" },
              { name: "Blog", href: "/blog" },
              { name: post.title },
            ]}
            tone="dark"
          />
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mt-2 mb-4">
            {post.category}
          </p>
          <h1 className="font-heading text-3xl lg:text-5xl font-bold leading-[1.1] mb-6 [text-wrap:balance] max-w-3xl">
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-white/70">
            <span>{post.author}</span>
            <span>·</span>
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" /> {formatDate(post.date)}
            </span>
            <span>·</span>
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" /> {post.readTime}
            </span>
          </div>
        </div>
      </section>

      <article className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4 max-w-6xl grid lg:grid-cols-[minmax(0,1fr)_340px] gap-10 lg:gap-14">
          {/* Main column */}
          <div className="min-w-0">
            {resolvedImage && (
              <img
                src={resolvedImage}
                alt={post.image ? post.title : heroImage.alt}
                className="w-full aspect-video object-cover rounded-md mb-10 bg-secondary"
               width={1200} height={675} />
            )}
            <ReviewedBy
              reviewer={post.author.split(",")[0]}
              credentials={post.author.includes(",") ? post.author.split(",").slice(1).join(",").trim() : undefined}
              date={post.dateModified ?? post.date}
            />

            {post.quickAnswer && (
              <QuickAnswer
                answer={post.quickAnswer.answer}
                bullets={post.quickAnswer.bullets}
              />
            )}

            {post.bodyHtml ? (
              <div
                className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-navy prose-h2:text-2xl lg:prose-h2:text-3xl prose-h2:mt-10 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-p:text-foreground/85 prose-p:leading-[1.75] prose-p:text-[17px] prose-a:text-gold hover:prose-a:text-gold-light prose-strong:text-navy prose-img:rounded-md prose-img:my-8 prose-ul:my-4 prose-li:my-1"
                dangerouslySetInnerHTML={{ __html: post.bodyHtml }}
              />
            ) : post.body ? (
              <RichContent
                body={post.body}
                midCta={
                  <div className="rounded-xl bg-gradient-navy text-white p-6 md:p-7 shadow-lg">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-gold mb-2">
                      Dealing with this right now?
                    </p>
                    <p className="font-heading text-xl md:text-2xl font-bold leading-tight mb-3">
                      Get a free 15-minute case review with a CPA.
                    </p>
                    <p className="text-white/80 text-sm mb-4 leading-relaxed">
                      No sales pitch. You'll leave knowing exactly where you stand — even if you don't hire us.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <a
                        href={`tel:${brand.phoneTel}`}
                        className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-navy-dark font-bold px-5 py-3 rounded-lg"
                      >
                        <Phone className="w-4 h-4" /> Call {brand.phone}
                      </a>
                      <Link
                        to="/contact"
                        className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 border border-white/25 text-white font-semibold px-5 py-3 rounded-lg"
                      >
                        Request a callback
                      </Link>
                    </div>
                  </div>
                }
              />
            ) : (
              <div className="space-y-6">
                {(post.sections ?? []).map((s, i) => (
                  <div key={i}>
                    {s.heading && (
                      <h2 className="font-heading text-2xl lg:text-3xl font-bold text-navy mb-4 mt-10 first:mt-0 leading-[1.2]">
                        {s.heading}
                      </h2>
                    )}
                    {s.paragraphs.map((p, j) => (
                      <p key={j} className="text-foreground/85 leading-[1.75] text-[17px] mb-4">
                        {p}
                      </p>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {post.sources && post.sources.length > 0 && (
              <AuthoritySources sources={post.sources} />
            )}

            <AuthorBio lastUpdated={formatDate(post.dateModified ?? post.date)} />

            <Link
              to="/blog"
              className="mt-12 inline-flex items-center gap-1 text-sm font-semibold text-navy hover:text-gold"
            >
              <ArrowLeft className="w-4 h-4" /> Back to blog
            </Link>
          </div>

          {/* Sidebar (stacks below on mobile) */}
          <BlogPostSidebar post={post} related={related} />
        </div>
      </article>

      <RelatedPosts posts={related} />

      <CTASection />
    </SiteLayout>
  );
};

export default BlogPostPage;
